import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgressBar from './CircularProgressBar';
import moment from 'moment';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import vehicleSchema from './forms/schemas/vehicle/schema.json';
import vehicleModels from './forms/schemas/vehicle/static_models.json';

import { COLORS, SPACING, TYPOGRAPHY } from '../../styles/theme';

import {
  useApi,
  useEvent,
  useUser,
  useCase,
  useNotification,
  useDialog,
  useLocalStorage,
  useCaseId,
  useGoogleMapCoordinates,
} from '../../hooks';
import { DynamicPageComponent } from './types';
import { CvLayout } from './CvLayout';
import {
  CustomerItem,
  VehicleItem,
  CallDetailsItem,
  LocationItem,
  Status,
  LocationCallLogMessage,
  CaseSteps,
  CaseStepsItem,
  CallLog,
  ErrorType,
  E911ConnectAppRequest,
} from '../../services/api/types';
import { LocationCard } from './LocationCard';
import { ReadonlyFormCard } from './ReadonlyFormCard';
import calldeSchema from './forms/schemas/calldetailsreadonly/schema.json';
import calldeUiSchema from './forms/schemas/calldetailsreadonly/uischema.json';
import { Customer } from './Customer';
import { Vehicle } from './Vehicle';
import { EventLogCard } from './EventLogCard';
import { DropPage } from '../DropPage';
import {
  LOCATION_LIST_OF_COUNTRY_CODES,
  STORAGE_CURRENT_CASE_ID,
  STORAGE_PREVIOUS_CASE_ID,
} from '../../constants';
import { SnackBarNotificationOptions } from '../../types/SnackBarNotificationOptions';

const localTheme = createTheme({
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: COLORS.ERROR_BORDER,
          backgroundColor: COLORS.WHITE,
          border: '1px solid #ddd',
          boxShadow: '-1px 4px 5px 0px #969696',
          padding: '10px',
          fontSize: '0.8rem',
          bottom: '10px',
        },
        arrow: {
          fontSize: '25px',
          color: COLORS.WHITE,
          '&::before': {
            background: COLORS.WHITE,
            border: '1px solid #ddd',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          boxSizing: 'border-box',
          background: '#fff',
          boxShadow:
            '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: 'rgba(0, 0, 0, 0.87)',
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
          backgroundColor: '#fff',
          borderRadius: '6px',
          boxSizing: 'border-box',
          boxShadow: '0 0 0 1000px #fff inset',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          // Remove autofill background color for Material UI textboxes
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px #fff inset',
            boxShadow: '0 0 0 1000px #fff inset',
            backgroundColor: '#fff !important',
            color: 'inherit',
            caretColor: 'inherit',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#0000008a !important',
          '&.Mui-focused': {
            color: '#0000008a !important',
          },
        },
      },
    },
  },
});
// MUI v5 styled component for Vehicle wrapper
const StyledVehicleWrapper = styled('div')(() => ({
  // Remove all margins and padding from wrapper
  margin: '0 !important',
  padding: '0 !important',
}));

const StyledContainer = styled(Grid)(() => ({
  padding: '1em',
  width: '100%',
}));

// Dialog styling components

const dialogTextStyles = {
  marginBottom: '0',
  padding: SPACING.DIALOG_PADDING,
  fontSize: TYPOGRAPHY.DIALOG_FONT_SIZE,
};

const StyledCheckIcon = styled(CheckCircleIcon)({
  color: '#388e3c',
  margin: 0,
  verticalAlign: 'middle',
});

const StyledErrorIcon = styled(ErrorIcon)(() => ({
  margin: 0,
  verticalAlign: 'middle',
  color: '#d32f2f',
}));

// MUI v5 styled components for various elements
const StyledActionButton = styled(Button)(() => ({
  color: COLORS.WHITE,
  backgroundColor: COLORS.PRIMARY_BLUE,
  paddingLeft: SPACING.BUTTON_PADDING_LEFT,
  '&:hover': {
    backgroundColor: COLORS.PRIMARY_BLUE,
  },
  '&.Mui-disabled': {
    backgroundColor: COLORS.PRIMARY_BLUE,
    opacity: '0.6',
    color: COLORS.WHITE,
  },
  marginRight: '15px',
}));

const StyledCallButton = styled(Button)(() => ({
  color: COLORS.WHITE,
  paddingLeft: SPACING.BUTTON_PADDING_LEFT,
  backgroundColor: COLORS.PRIMARY_BLUE,
  '&:hover': {
    backgroundColor: COLORS.PRIMARY_BLUE,
  },
  '&.Mui-disabled': {
    backgroundColor: COLORS.PRIMARY_BLUE,
    color: COLORS.WHITE,
    opacity: '0.6',
  },
}));

export interface CustomerContextProps {
  customerProps: CustomerItem;
  setCustomerProps: React.Dispatch<React.SetStateAction<CustomerItem>>;
}

export interface VehicleContextProps {
  vehicleProps: VehicleItem | null;
  setVehicleProps: React.Dispatch<React.SetStateAction<VehicleItem>>;
}

export interface CallDetailsContextProps {
  callDetailsProps: CallDetailsItem;
  setCallDetailsProps: React.Dispatch<React.SetStateAction<CallDetailsItem>>;
}

export const ActiveCase: DynamicPageComponent = () => {
  const { api } = useApi();
  const navigate = useNavigate();
  const { withLocalStorage } = useLocalStorage();
  const { userExtension } = useUser();
  const { showNotification, closeSnackbar } = useNotification();
  const { setEventLogProps } = useEvent();
  const { openDialog } = useDialog();
  const {
    caseData,
    setCaseData,
    call911Status,
    isLocationValidated,
    setIsLocationValidated,
    caseTerminated,
  } = useCase();
  const location = useLocation();
  const { setActiveCaseId } = useCaseId();
  const { googleMapCoordinatesData } = useGoogleMapCoordinates();

  const [customerProps, setCustomerProps] = useState<CustomerItem>({});
  const [vehicleProps, setVehicleProps] = useState<VehicleItem>({});
  const [callDetailsProps, setCallDetailsProps] = useState<CallDetailsItem>({});
  const [place, setPlace] = useState<LocationItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [locationLoaded, setLocationLoaded] = useState<boolean>(false);
  const queryString = location.search;
  const urlParams = new URLSearchParams(queryString);
  const [caseId, setCaseId] = useState<string>(
    urlParams.get('caseid') || withLocalStorage(STORAGE_CURRENT_CASE_ID) || '',
  );

  const phoneNumber: any = urlParams.get('phone_number');
  const [enableCall911, setEnableCall911] = useState<boolean>(true);
  const [enableE911ConnectAppButton, setEnableE911ConnectAppButton] =
    useState<boolean>(false);

  const [dispatchInprogress, setDispatchInprogress] = useState(false);
  const [enableDispatch, setEnableDispatch] = useState(false);
  const [call911ShowTooltip, setcall911ShowTooltip] = useState(false);
  const [isCountryCodeRestrict, setIsCountryCodeRestrict] =
    useState<boolean>(true);
  const [vehicleSchemaState, setVehicleSchemaState] = useState(vehicleSchema);

  const showCalllogMessage = (
    level: ErrorType,
    message: any,
    stepType: string,
    case_id: string = caseId,
  ) => {
    const title = message.title ? message.title : message;
    const desc = message.description ? message.description : '';
    const log = {
      level: level,
      message: title,
      date: moment(new Date()).format('MM/DD/YYYY HH:mm'),
      description: desc,
    };
    setEventLogProps((eventLogs: any) => [log, ...eventLogs]);
    const calllog = desc ? `:${desc}` : '';
    const caseStepsData: CaseStepsItem = {
      caseId: case_id,
      stepType: stepType,
      callLog: `${title}${calllog}`,
      callLogType: level,
    };
    api.post_casestepsdata(caseStepsData);
  };

  useEffect(() => {
    // Pull vehicle makes
    const pullVehicleMakes = async (
      brand: string,
      vehicleData: VehicleItem,
    ) => {
      let models_list: string[] = (await api.get_vehicle_make(brand)) || [];
      if (models_list.length === 0) {
        models_list = (vehicleModels as any)[brand.toLowerCase()];
      }
      // Remove duplicates
      models_list = Array.from(new Set(models_list));

      const allModels = [{ label: 'None', value: 'None' }];
      for (const model of models_list) {
        allModels.push({ label: model, value: model });
      }

      // year manipulation
      const startYear = 1992;
      const thisYear = new Date().getFullYear();
      const allYears: { label: string; value: string }[] = [];
      allYears.push({ label: 'None', value: 'None' });
      for (let x = startYear; x <= thisYear; x++) {
        allYears.push({ label: x.toString(), value: x.toString() });
      }

      // Create a new schema object to trigger re-render
      const updatedSchema = {
        ...vehicleSchema,
        properties: {
          ...vehicleSchema.properties,
          model: {
            ...vehicleSchema.properties.model,
            enum: Array.from(new Set(allModels.map(m => m.value))),
          },
          year: {
            ...vehicleSchema.properties.year,
            enum: Array.from(new Set(allYears.map(year => year.value))),
          },
        },
      };
      setVehicleSchemaState(updatedSchema);

      // colors manipulation
      const colors = vehicleSchema.properties.color.enum;
      if (vehicleData.color === '' || vehicleData.color === 'None') {
        vehicleData.color = 'None';
      }
      if (!colors.includes(vehicleData.color ? vehicleData.color : '')) {
        vehicleData.color = 'Other';
      }
      vehicleData.brand = brand.toLowerCase();
      setVehicleProps(vehicleData);
    };

    let isMounted = true;
    setLoading(true);
    setLocationLoaded(false);
    if (userExtension) {
      if (
        caseData &&
        caseData.status !== 0 &&
        Object.keys(caseData?.customer).length !== 0
      ) {
        if (isMounted) {
          setCustomerProps(caseData?.customer);
          setPlace(caseData?.location);
          setCallDetailsProps(caseData?.callDetails);
          setLoading(false);
          setLocationLoaded(true);
          pullVehicleMakes(
            caseData?.callDetails.program ? caseData?.callDetails.program : '',
            caseData?.vehicle,
          );
          const currentCaseId = caseData?.callDetails?.caseId
            ? caseData?.callDetails?.caseId
            : '';
          if (caseId !== currentCaseId) {
            setCaseId(currentCaseId);
            if (caseTerminated !== true) {
              withLocalStorage(STORAGE_CURRENT_CASE_ID, currentCaseId);
              setActiveCaseId(currentCaseId);
              setEventLogProps(caseData.casesteps);
              showCalllogMessage(
                'INFO',
                CallLog.INITIALIZATION,
                CaseSteps.INITIALIZATION,
                currentCaseId,
              );
              showCalllogMessage(
                'INFO',
                CallLog.CASE_RETRIEVED,
                CaseSteps.CASE_RETRIEVED,
                currentCaseId,
              );
            }
          }
        }
      } else if (caseTerminated === true) {
        withLocalStorage(STORAGE_PREVIOUS_CASE_ID, caseId);
        setEnableE911ConnectAppButton(false);
        navigate(`/app/landing`);
      } else {
        const addCaseDetailsFromApi = async () => {
          setLoading(true);
          const resFromApi = await api.get_casedata(
            caseId,
            phoneNumber,
            userExtension,
          );

          if (isMounted) {
            if (
              resFromApi &&
              resFromApi.status === Status.SUCCESS &&
              resFromApi.app_code === 'CVP'
            ) {
              if (!resFromApi.callDetails.caseId) {
                navigate(`/app/landing`);
              } else {
                const currentCaseId = resFromApi?.callDetails?.caseId
                  ? resFromApi?.callDetails?.caseId
                  : '';
                setCaseId(currentCaseId);
                withLocalStorage(STORAGE_CURRENT_CASE_ID, currentCaseId);
                setActiveCaseId(currentCaseId);
                setEventLogProps(resFromApi?.casesteps);
                showCalllogMessage(
                  'INFO',
                  CallLog.CASE_RETRIEVED,
                  CaseSteps.CASE_RETRIEVED,
                  currentCaseId,
                );
                setCustomerProps(resFromApi?.customer);
                setPlace(resFromApi?.location);
                pullVehicleMakes(
                  resFromApi?.callDetails.program
                    ? resFromApi?.callDetails.program
                    : '',
                  resFromApi?.vehicle,
                );
                setLocationLoaded(true);
                setCallDetailsProps(resFromApi?.callDetails);
                setLoading(false);
                setCaseData(resFromApi);
              }
            } else {
              navigate(`/app/landing`);
            }
          }
        };

        if (caseId && caseId !== null) {
          addCaseDetailsFromApi();
        } else {
          navigate(`/app/landing`);
        }

        if (caseId === null && caseData === null) {
          navigate(`/app/landing`);
        }
      }
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, phoneNumber, userExtension, caseData]);

  // use for checking customer name and phone number is empty
  useEffect(() => {
    const checkCustomerData =
      !caseData?.customer?.customerName || !caseData?.customer?.phoneNumber
        ? false
        : true;
    setEnableDispatch(checkCustomerData);
  }, [caseData?.customer?.customerName, caseData?.customer?.phoneNumber]);

  // Handle the form data in the parent component to isolate form concerns.
  const saveCustomerHandler = async (data: CustomerItem) => {
    data.caseId = caseId;
    const res = await api.put_customerdata(data);
    if (res) {
      setCustomerProps(res);
      showCalllogMessage(
        'INFO',
        CallLog.CUSTOMER_UPDATE_SUCCESS,
        CaseSteps.CUSTOMER_VALIDATED,
      );

      if (data && caseData) {
        const caseDataCopy = caseData;
        caseDataCopy.customer = data;
        setCaseData(caseDataCopy);
        const checkCustomerData =
          !caseData?.customer.customerName || !caseData?.customer.phoneNumber
            ? false
            : true;
        setEnableDispatch(checkCustomerData);
      }
    } else {
      showCalllogMessage(
        'ERROR',
        CallLog.CUSTOMER_UPDATE_FAILED,
        CaseSteps.CUSTOMER_VALIDATION_FAILED,
      );
    }
  };

  const saveVehicleHandler = async (data: VehicleItem) => {
    data.caseId = caseId;
    const res = await api.put_vehicledata(data);
    if (res) {
      setVehicleProps(res);
      showCalllogMessage(
        'INFO',
        CallLog.VEHICLE_UPDATE_SUCCESS,
        CaseSteps.VEHICLE_VALIDATED,
      );
      if (data && caseData) {
        const caseDataCopy = caseData;
        caseDataCopy.vehicle = data;
        setCaseData(caseDataCopy);
      }
    } else {
      showCalllogMessage(
        'ERROR',
        CallLog.VEHICLE_UPDATE_FAILED,
        CaseSteps.VEHICLE_VALIDATION_FAILED,
      );
    }
  };

  const onPlaceChangedHandler = async (
    data: LocationItem | null,
    logMessage: LocationCallLogMessage,
  ) => {
    setPlace(data);
    if (logMessage.description) {
      setcall911ShowTooltip(true);
    } else {
      setcall911ShowTooltip(false);
    }
    if (data === null || data?.latitude === null || data?.longitude === null) {
      showCalllogMessage(
        'ERROR',
        logMessage,
        CaseSteps.LOCATION_VALIDATION_FAILED,
      );
      setEnableCall911(false);
      setEnableE911ConnectAppButton(false);
    } else if (
      data?.latitude !== caseData?.location?.latitude ||
      data?.longitude !== caseData?.location?.longitude
    ) {
      showCalllogMessage('INFO', logMessage, CaseSteps.LOCATION_VALIDATED);
    } else if (isLocationValidated === false) {
      setIsLocationValidated(true);
      showCalllogMessage('INFO', logMessage, CaseSteps.LOCATION_VALIDATED);
    }

    if (data) {
      data.caseId = caseId;
      await api.post_locationdata(data);
      if (caseData) {
        const caseDataCopy = caseData;
        caseDataCopy.location = data;
        setCaseData(caseDataCopy);
        setEnableE911ConnectAppButton(true);
      }
      if (call911Status === 'INACTIVE') {
        setEnableCall911(true);
        setEnableE911ConnectAppButton(true);
      }
    }

    closeSnackbar();
  };

  const isCoordinateInList = (
    inputLatitude: number | undefined,
    inputLongitude: number | undefined,
  ): boolean => {
    if (
      googleMapCoordinatesData &&
      googleMapCoordinatesData.google_map_coordinates &&
      googleMapCoordinatesData.is_google_map_coordinates &&
      Object.keys(googleMapCoordinatesData.google_map_coordinates).length > 0 &&
      !isCountryCodeRestrict
    ) {
      for (const latLong of googleMapCoordinatesData.google_map_coordinates) {
        if (
          (inputLatitude &&
            inputLongitude &&
            latLong.latitude === inputLatitude &&
            latLong.longitude === inputLongitude) ||
          (place &&
            place?.latitude &&
            place?.longitude &&
            place?.country &&
            LOCATION_LIST_OF_COUNTRY_CODES.includes(place?.country))
        ) {
          return true;
        }
      }
    } else if (
      googleMapCoordinatesData &&
      googleMapCoordinatesData.google_map_coordinates &&
      googleMapCoordinatesData.is_google_map_coordinates &&
      Object.keys(googleMapCoordinatesData.google_map_coordinates).length ===
        0 &&
      isCountryCodeRestrict
    ) {
      if (
        place &&
        place?.latitude &&
        place?.longitude &&
        place?.country &&
        LOCATION_LIST_OF_COUNTRY_CODES.includes(place?.country)
      ) {
        return true;
      }
      return false;
    } else if (
      googleMapCoordinatesData &&
      googleMapCoordinatesData.google_map_coordinates === undefined &&
      !googleMapCoordinatesData.is_google_map_coordinates &&
      isCountryCodeRestrict
    ) {
      return true;
    }
    return false;
  };

  const isGoogleMapCoordinatesAllowed = isCoordinateInList(
    place?.latitude,
    place?.longitude,
  );

  /*
    default is true i.e if isCountryCodeRestrict is true then allowed only listed countries (USA & canada)
    else false no restrictions.
    this is only sued for checking if country is restricted or not.
  */
  useEffect(() => {
    setIsCountryCodeRestrict(true);
    if (
      googleMapCoordinatesData &&
      googleMapCoordinatesData.google_map_coordinates &&
      googleMapCoordinatesData.is_google_map_coordinates
    ) {
      if (
        Object.keys(googleMapCoordinatesData.google_map_coordinates).length > 0
      ) {
        setIsCountryCodeRestrict(false);
      }
    }
  }, [googleMapCoordinatesData]);

  const handleDispatchClick = async () => {
    setDispatchInprogress(true);
    showCalllogMessage(
      'WARN',
      CallLog.CASE_CREATION_INPROGRESS,
      CaseSteps.RSA_DISPATCH_REQUEST,
    );
    let dispatchMessage = '';
    let dispatchSuccess = false;
    const response = await api.post_dispatchcase(caseId, userExtension);
    if (response && (response.status === Status.SUCCESS || Status.CREATED)) {
      showCalllogMessage(
        'INFO',
        CallLog.DRAFT_JOB_CREATED,
        CaseSteps.RSA_DISPATCH_SUCCESS,
      );
      dispatchMessage = `Job ID ${response.result?.job_id} is created in Swoop. Please continue with the dispatch using Swoop.`;
      dispatchSuccess = true;
    } else {
      showCalllogMessage(
        'ERROR',
        CallLog.DRAFT_JOB_NOT_CREATED,
        CaseSteps.RSA_DISPATCH_FAILED,
      );
      dispatchMessage =
        'The case could not be created in Swoop, please create a job/case in Swoop manually and continue the dispatch';
    }
    setDispatchInprogress(false);
    openDialog({
      children: (
        <>
          {dispatchSuccess ? <StyledCheckIcon /> : <StyledErrorIcon />}
          <p style={dialogTextStyles}>{dispatchMessage}</p>{' '}
        </>
      ),
      id: 'dispatch-dialog',
      btnCancel: { show: false, text: '' },
      btnOk: { show: true, text: 'OK' },
      title: { show: true, text: 'Dispatch Case' },
    });
  };

  const call911TooltipErrorMessage = () => {
    let message: string;
    if (!place?.latitude && !place?.longitude && !call911ShowTooltip) {
      message = 'Address is Required';
    } else if (!place && !enableCall911 && call911ShowTooltip) {
      message = 'Check For Errors';
    } else if (
      (place?.country &&
        !LOCATION_LIST_OF_COUNTRY_CODES.includes(place?.country) &&
        isCountryCodeRestrict &&
        !enableCall911) ||
      (place?.country &&
        place?.latitude &&
        place?.longitude &&
        !isGoogleMapCoordinatesAllowed &&
        isCountryCodeRestrict &&
        !enableCall911) ||
      (place?.country &&
        place?.latitude &&
        place?.longitude &&
        !isGoogleMapCoordinatesAllowed &&
        !isCountryCodeRestrict &&
        !enableCall911)
    ) {
      message = 'Check For Errors';
    } else {
      message = '';
    }
    return message;
  };

  const handleE911ConnectApp = async () => {
    if (!place?.latitude || !place?.longitude) {
      showCalllogMessage(
        'ERROR',
        CallLog.ADDRESS_REQUIRED,
        CaseSteps.ADDRESS_REQUIRED,
      );
      const snackBaroptions: SnackBarNotificationOptions = {
        type: 'error',
        title: `Address/Location is required for calling 911`,
      };
      setEnableE911ConnectAppButton(false);
      showNotification(snackBaroptions);
    } else if (
      (place?.country &&
        !LOCATION_LIST_OF_COUNTRY_CODES.includes(place?.country) &&
        isCountryCodeRestrict) ||
      (place?.country &&
        place?.latitude &&
        place?.longitude &&
        !isGoogleMapCoordinatesAllowed &&
        isCountryCodeRestrict) ||
      (place?.country &&
        place?.latitude &&
        place?.longitude &&
        !isGoogleMapCoordinatesAllowed &&
        !isCountryCodeRestrict)
    ) {
      const snackBaroptions: SnackBarNotificationOptions = {
        type: 'error',
        title: `Location outside of USA or Canada.
           Please Update the address or follow emergency dispatch process for the country`,
      };
      showNotification(snackBaroptions);
      setEnableE911ConnectAppButton(false);
    } else if (place.latitude && place.longitude) {
      setEnableE911ConnectAppButton(true);
      showCalllogMessage(
        'INFO',
        CallLog.E911_CONNECT_APP_CASE_CREATION_IN_PROGRESS,
        CaseSteps.E911_CONNECT_APP_CASE_CREATION_IN_PROGRESS,
      );
      const lat = place.latitude;
      const lng = place.longitude;

      let vehicleColor = vehicleProps.color ? vehicleProps.color : null;
      if (vehicleColor === 'None') {
        vehicleColor = null;
      }

      let vehicleYear: string | null | undefined =
        typeof vehicleProps.year === 'number'
          ? vehicleProps.year.toString()
          : vehicleProps.year;
      if (vehicleYear === 'None') {
        vehicleYear = null;
      }
      const e911ConnectAppRequestData: E911ConnectAppRequest = {
        agentExtension: userExtension,
        incidentId: caseId,
        customerNumber: customerProps.phoneNumber,
        customerName: customerProps.customerName,
        vehicle: {
          color: vehicleColor,
          year: vehicleYear,
          vin: vehicleProps.vin,
        },
        location: {
          latitude: `${lat}`,
          longitude: `${lng}`,
        },
      };
      const res = await api.post_e911connectapp_createcase(
        e911ConnectAppRequestData,
      );
      if (res && res.status === 200) {
        setEnableE911ConnectAppButton(false);
        showCalllogMessage(
          'INFO',
          CallLog.E911_CONNECT_APP_CASE_CREATED,
          CaseSteps.E911_CONNECT_APP_CASE_CREATED,
        );

        if (res.ui_url) {
          showCalllogMessage(
            'INFO',
            CallLog.E911_CONNECT_APP_LAUNCH_IN_PROGRESS,
            CaseSteps.E911_CONNECT_APP_LAUNCH_IN_PROGRESS,
          );
          window.open(res.ui_url, '_blank');

          setTimeout(() => {
            setEnableE911ConnectAppButton(true);
          }, 5000);
        }
      } else {
        setEnableE911ConnectAppButton(false);
        showCalllogMessage(
          'ERROR',
          CallLog.E911_CONNECT_APP_CASE_CREATION_FAILED,
          CaseSteps.E911_CONNECT_APP_CASE_CREATION_FAILED,
        );

        setTimeout(() => {
          setEnableE911ConnectAppButton(true);
        }, 5000);
      }
    }
  };

  if (loading) {
    return <DropPage>Loading Active Case</DropPage>;
  }

  return (
    <CvLayout
      headerTitle="Connected Vehicle Portal"
      viewActiveCase="VIEW ACTIVE CASE"
    >
      <Fragment>
        <ThemeProvider theme={localTheme}>
          <StyledContainer container justifyContent="center" spacing={1}>
            <Grid item sm={6}>
              <ReadonlyFormCard
                data={callDetailsProps}
                title="Call Details"
                uischema={calldeUiSchema}
                schema={calldeSchema}
              />
              <Customer onSave={saveCustomerHandler} data={customerProps} />
            </Grid>
            <Grid item sm={6}>
              <LocationCard
                title="Location"
                onPlaceChanged={onPlaceChangedHandler}
                data={place}
                locationLoaded={locationLoaded}
                isCountryCodeRestrict={isCountryCodeRestrict}
              />
            </Grid>
            <Grid item sm={6}>
              <StyledVehicleWrapper>
                <Vehicle
                  onSave={saveVehicleHandler}
                  data={vehicleProps}
                  vehicleSchema={vehicleSchemaState}
                />
              </StyledVehicleWrapper>
            </Grid>
            <Grid item sm={6}>
              <EventLogCard title="Call Log" scrollHeight={162} />
            </Grid>
            <Grid item sm={6} style={{ marginTop: '15px', textAlign: 'end' }}>
              <Tooltip
                title={
                  !enableDispatch
                    ? 'Customer Name and Customer Phone are required'
                    : ''
                }
                arrow
                placement="top"
              >
                <span>
                  <StyledActionButton
                    variant="contained"
                    color="primary"
                    onClick={handleDispatchClick}
                    data-testid="dispatch-button"
                    disabled={!enableDispatch}
                  >
                    Dispatch
                  </StyledActionButton>
                </span>
              </Tooltip>
            </Grid>
            <Grid
              item
              sm={6}
              style={{ marginTop: '15px', paddingLeft: '25px' }}
            >
              <>
                <Tooltip
                  title={call911TooltipErrorMessage()}
                  arrow
                  placement="top"
                >
                  <span>
                    <StyledCallButton
                      variant="contained"
                      color="primary"
                      onClick={handleE911ConnectApp}
                      data-testid="e911connectApp-button"
                      disabled={!enableE911ConnectAppButton}
                    >
                      Connect E911
                    </StyledCallButton>
                  </span>
                </Tooltip>
              </>
            </Grid>
            <div>
              {dispatchInprogress && (
                <div className="MuiProgressOverlay">
                  <CircularProgressBar />
                </div>
              )}
            </div>
          </StyledContainer>
        </ThemeProvider>
      </Fragment>
    </CvLayout>
  );
};
